/**
 * @file
 * Old AMD key: `ac/jira/workflow-post-function-utils`
 */

import { escapeSelector } from '../../../common';
import type { ConnectCallback } from '../../../types';
import type { WorkflowSubmissionCallback, ListenerEventCallback } from './types';

/**
 * Similar to jQuery's $(...).delegate(...)
 * @see {@link https://thomaswilburn.github.io/viz-book/js-jquery.html Source}
 */
const delegate = (
	container: Node,
	selector: string,
	type: keyof HTMLElementEventMap,
	listener: ListenerEventCallback<'click'>,
	options?: boolean | AddEventListenerOptions,
) => {
	container.addEventListener(
		type,
		// According to this document https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Functions/arguments,
		// > The arguments object is a local variable available within all non-arrow functions.
		// Since we are using arguments in this function, so we cannot convert this to arrow function.
		function (event) {
			const { target } = event;
			if (target && target instanceof Element) {
				const matched = target.closest(selector);

				if (matched) {
					// @ts-expect-error TS2345: Argument of type 'IArguments' is not assignable to parameter of type '[]'.
					// eslint-disable-next-line prefer-rest-params
					listener.apply(matched, arguments);
				}
			}
		},
		options,
	);
};

export function getWorkflowElementName(moduleType?: string): string | null {
	if (
		moduleType === 'jiraWorkflowPostFunctions' &&
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		document.querySelectorAll("input[name='postFunction.id']").length > 0
	) {
		return 'postFunction';
	}

	if (
		moduleType === 'jiraWorkflowConditions' &&
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		document.querySelectorAll("input[name='condition.id']").length > 0
	) {
		return 'condition';
	}

	if (
		moduleType === 'jiraWorkflowValidators' &&
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		document.querySelectorAll("input[name='validator.id']").length > 0
	) {
		return 'validator';
	}

	return null;
}

export function getWorkflowElementId(moduleType?: string): string | null {
	if (!moduleType) {
		throw new Error('Missing module type');
	}

	const inputName = escapeSelector(getWorkflowElementName(moduleType));
	const selector = `input[name='${inputName}.id']`;

	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	const elem: HTMLInputElement | null = document.querySelector(selector);

	return elem && elem.value;
}

export function workflowElementConfigInput(
	moduleType: string,
	workflowElementId: string | null,
	val?: string,
): string | undefined {
	const idPrefix = escapeSelector(getWorkflowElementName(moduleType));
	const idPostfix = escapeSelector(workflowElementId);

	const selector = `#${idPrefix}-config-${idPostfix}`;

	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	const element: HTMLInputElement | null = document.querySelector(selector);

	if (val && element) {
		element.value = val;
	}

	return element ? element.value : undefined;
}

export function isOnWorkflowElementPage(moduleType: string | undefined): boolean {
	return Boolean(getWorkflowElementName(moduleType));
}

export function registerSubmissionButton(
	moduleType: string | undefined,
	workflowElementId: string | null,
	callback: (workflowSubmissionCallback: WorkflowSubmissionCallback) => void,
	repeat?: boolean,
) {
	if (!moduleType) {
		throw new Error('Missing module type');
	}

	if (!isOnWorkflowElementPage(moduleType)) {
		throw new Error('Not on a workflow configuration page');
	}

	let done = false;

	delegate(document, '#add_submit, #update_submit', 'click', (e: MouseEvent) => {
		if (!done || repeat) {
			e.preventDefault();

			const workflowSubmissionCallback: WorkflowSubmissionCallback = (either) => {
				if (either.valid) {
					workflowElementConfigInput(moduleType, workflowElementId, either.value);
					done = true;

					const { target } = e;

					if (target && 'click' in target) {
						// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
						(target as HTMLElement).click();
					}
				}
			};

			callback(workflowSubmissionCallback);
		}
	});
}

/**
 * Check if extension contains workflow element Id.
 */
export function isWorkflowElementExtension(extension: ConnectCallback['_context']['extension']) {
	if (
		extension &&
		extension.options &&
		extension.options.productContext &&
		extension.options.moduleType
	) {
		const ctx = extension.options.productContext;
		const { moduleType } = extension.options;

		if (
			moduleType &&
			ctx[`${getWorkflowElementName(moduleType)}.id`] &&
			ctx[`${getWorkflowElementName(moduleType)}.id`].length > 5
		) {
			return true;
		}
	}

	return false;
}
